import { ComponentProps, Fragment, ReactNode, useMemo, useRef } from 'react';
import { HeaderAction } from './components/header-action';
import styles from './index.module.scss';
import urlLogo from '@/assets/images/logo.svg?url';
import { Divider } from '@/components/divider';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { useWindowWidth } from '@/hooks/useWindowSize';
import { Menu } from '@base-ui/react/menu';
import { UserPopup } from '@/components/user-popup';
import { IHeaderAction } from './types';

interface IProps extends ComponentProps<'header'> {
  actions?: IHeaderAction[];
  projectName?: string;
  /** 居中的导航区，供管理后台这类带页签的页面使用 */
  nav?: ReactNode;
}

const MINIMUM_ACTION_CHAR_SIZE = 2;
const MINIMUM_ACTION_WIDTH = 44;
const ACTION_EXTRA_WIDTH_PER_CHAR = 10;
const DEFAULT_ACTION_WIDTH = 60;
const HEADER_GAP = 64;

export default function Header({ className = '', actions = [], projectName, nav }: IProps) {
  const infoRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const { width: nameWidth } = useResizeObserver({ ref: infoRef });
  const { width: userWidth } = useResizeObserver({ ref: userRef });
  const windowWidth = useWindowWidth();

  const canShowCount = useMemo(() => {
    const actionsWidth = actions.reduce((acc, action) => {
      return (
        acc +
        Math.max(action.label.length - MINIMUM_ACTION_CHAR_SIZE, 0) * ACTION_EXTRA_WIDTH_PER_CHAR +
        MINIMUM_ACTION_WIDTH
      );
    }, 0);

    const actionWidth = actions.length === 0 ? DEFAULT_ACTION_WIDTH : actionsWidth / actions.length;

    return Math.max(Math.floor((windowWidth - nameWidth - userWidth - HEADER_GAP * 2) / (actionWidth + 10)), 0);
  }, [windowWidth, nameWidth, userWidth, actions]);

  const actionStyle = useMemo(() => {
    return {
      '--actions-start': `${nameWidth + HEADER_GAP}px`,
      '--actions-width': `${windowWidth - nameWidth - userWidth - HEADER_GAP * 2}px`
    } as React.CSSProperties;
  }, [windowWidth, nameWidth, userWidth]);

  return (
    <header className={`${styles.container} ${className}`}>
      <div className={styles.info} ref={infoRef}>
        <div className={styles.logo}>
          <img src={urlLogo} alt="S-LiDAR" />
        </div>
        {projectName && (
          <>
            <Divider orientation="vertical" className={styles.divider} />
            <div className={styles.projectName}>{projectName}</div>
          </>
        )}
      </div>
      {nav && <div className={styles.nav}>{nav}</div>}
      {actions.length > 0 && (
        <div className={styles.actions} style={actionStyle}>
          {actionsRenderer(actions.slice(0, canShowCount))}
          {canShowCount < actions.length && (
            <Menu.Root orientation="horizontal">
              <Menu.Trigger openOnHover delay={0} className={styles.moreActionsTrigger}>
                …
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Positioner className={styles.moreActionsPositioner} side="bottom" align="start" alignOffset={-28}>
                  <Menu.Popup className={styles.moreActionsPopup}>
                    {actionsRenderer(actions.slice(canShowCount), true)}
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          )}
        </div>
      )}
      <div className={styles.userInfo} ref={userRef}>
        <UserPopup />
      </div>
    </header>
  );
}

function actionsRenderer(list: IHeaderAction[], isMenuItem = false) {
  return list.map((action, index, list) => {
    if (list.length > 1 && action.id === 'needDivider' && index > 0) {
      return (
        <Fragment key={action.id}>
          {isMenuItem ? (
            <Menu.Separator className={styles.actionDivider} orientation="vertical" render={<Divider />} />
          ) : (
            <Divider orientation="vertical" className={styles.actionDivider} />
          )}
          <HeaderAction action={action} isMenuItem={isMenuItem} />
        </Fragment>
      );
    } else {
      return <HeaderAction key={action.id} action={action} isMenuItem={isMenuItem} />;
    }
  });
}
